/**
 * MCP API request/response schemas (Phase 2 — M2.7).
 *
 * Endpoints:
 *   GET    /api/v1/connections              → list MCP instances
 *   POST   /api/v1/connections              → create instance
 *   GET    /api/v1/connections/:id          → instance detail
 *   DELETE /api/v1/connections/:id          → delete instance
 *   GET    /api/v1/mcp/servers              → list available servers
 *   POST   /api/v1/mcp/bindings             → create binding
 *   DELETE /api/v1/mcp/bindings/:id         → delete binding
 *
 * Workers-and-browser-safe. No Node-only imports.
 */
import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";
// ─── MCP Server ───────────────────────────────────────────────────────────────
export const McpServerKindSchema = z.enum(["public", "platform", "tenant_hosted"]);
export const McpServerVisibilitySchema = z.enum(["global", "tenant"]);
export const McpServerResponseSchema = z.object({
    id: UuidSchema,
    name: z.string(),
    kind: McpServerKindSchema,
    uri_template: z.string(),
    schema: z.record(z.unknown()),
    visibility: McpServerVisibilitySchema,
    tenant_id: UuidSchema.nullable(),
    created_at: IsoTimestampSchema,
});
export const McpServersListResponseSchema = z.object({
    items: z.array(McpServerResponseSchema),
});
// ─── MCP Instance (Connection) ────────────────────────────────────────────────
export const McpInstanceHealthSchema = z.enum(["healthy", "degraded", "down", "unknown"]);
export const CreateMcpInstanceRequestSchema = z.object({
    server_id: UuidSchema,
    name: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9-_]+$/, "Name must be lowercase alphanumeric with hyphens/underscores"),
    config: z.record(z.unknown()).default({}),
    secrets_refs: z.array(z.string().min(1).max(64)).max(16).default([]),
});
export const McpInstanceResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    server_id: UuidSchema,
    name: z.string(),
    config: z.record(z.unknown()),
    secrets_refs: z.array(z.string()),
    health: McpInstanceHealthSchema,
    last_health_check: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
});
export const McpInstancesListResponseSchema = z.object({
    items: z.array(McpInstanceResponseSchema),
});
// ─── MCP Binding ──────────────────────────────────────────────────────────────
export const CreateMcpBindingRequestSchema = z.object({
    agent_id: UuidSchema,
    agent_version: z.number().int().positive(),
    instance_id: UuidSchema,
    allowed_tools: z.array(z.string().min(1)).min(1).max(64),
    scope: z.record(z.unknown()).default({}),
});
export const McpBindingResponseSchema = z.object({
    id: UuidSchema,
    agent_id: UuidSchema,
    agent_version: z.number().int(),
    instance_id: UuidSchema,
    allowed_tools: z.array(z.string()),
    scope: z.record(z.unknown()),
    created_at: IsoTimestampSchema,
});
export const McpBindingsListResponseSchema = z.object({
    items: z.array(McpBindingResponseSchema),
});
// ─── MCP Calls (activity log) ─────────────────────────────────────────────────
export const McpCallResponseSchema = z.object({
    id: UuidSchema,
    run_id: UuidSchema,
    instance_id: UuidSchema,
    tool: z.string(),
    status: z.enum(["ok", "denied", "error", "timeout"]),
    deny_reason: z.string().nullable(),
    latency_ms: z.number().int().nullable(),
    cost_micros: z.string(), // bigint serialized as string
    at: IsoTimestampSchema,
});
export const McpCallsListResponseSchema = z.object({
    items: z.array(McpCallResponseSchema),
});
// ─── Gateway token claim (minted by orchestrator, verified by gateway) ────────
export const GatewayTokenClaimsSchema = z.object({
    /** Standard JWT: issued-at (Unix seconds). */
    iat: z.number(),
    /** Standard JWT: expires-at (Unix seconds). Defaults to iat + 3600 (1 hour). */
    exp: z.number(),
    /** The run id this token is bound to. */
    run_id: z.string().uuid(),
    /** Tenant scope. Gateway verifies tool calls are allowed for this tenant. */
    tenant_id: z.string().uuid(),
    /** Executor identity (e.g. 'worker', 'agents_sdk'). */
    executor_id: z.string(),
    /** Allowed MCP instance ids. Gateway rejects calls to unlisted instances. */
    allowed_instance_ids: z.array(z.string().uuid()),
});
//# sourceMappingURL=mcp.js.map